import type { ReactNode } from 'react';

/**
 * Slide toggle (switch) for on/off settings. Replaces bare checkboxes, whose
 * checked-vs-"Off"-label pairing left users unsure whether ticking the box
 * turned a feature on or off. The control itself shows state (knob slides,
 * track fills accent when on), so we never render an ambiguous "On"/"Off" word
 * next to it.
 *
 * Accessible: rendered as a real <button role="switch"> with aria-checked, so
 * it's keyboard-operable (Space/Enter) and announced correctly by screen
 * readers. The visible label sits beside the track and is click-through to the
 * button via the wrapping <label>-like layout.
 */
export default function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className={`toggle-field${disabled ? ' toggle-disabled' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={`toggle-switch${checked ? ' on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="toggle-knob" aria-hidden="true" />
      </button>
      <span className="toggle-label" onClick={() => !disabled && onChange(!checked)}>
        <span className="toggle-label-text">{label}</span>
        {description && <span className="toggle-label-desc">{description}</span>}
      </span>
    </div>
  );
}
